import { useMemo } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Link, useNavigate } from 'react-router'
import { trpc } from '@/config/trpc'
import env from '@/config/env'
import {
  freePlan,
  plans,
  type PricingPlan,
  standardPlan,
  proTrialPlan,
} from '@backend/lib/config/pricing'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { formatDate } from '@/lib/utils'

export function AccountSubscriptionPage() {
  const navigate = useNavigate()

  const { data: userSubscription } = trpc.userSubscription.get.useQuery()
  const { data: subscription } = trpc.stripe.isUserSubscribed.useQuery(undefined, {
    enabled: Boolean(userSubscription?.isSubscribed && userSubscription.source === 'stripe'),
  })

  const subscriptionPlan = useMemo<PricingPlan | null>(() => {
    if (subscription && subscription.isSubscribed) {
      return subscription.subscription ?? freePlan
    }
    if (userSubscription?.isSubscribed) {
      if (userSubscription.isTrial) return proTrialPlan
      // Non-Stripe entitlements (appsumo, magig-granted enterprise, ...) carry
      // their plan in the tier — look it up instead of assuming Standard.
      return plans[userSubscription.tier] ?? standardPlan
    }
    return freePlan
  }, [subscription, userSubscription])

  const isTrial = userSubscription?.isSubscribed && userSubscription.isTrial
  const trialExpiresAt = userSubscription?.isSubscribed ? userSubscription.expiresAt : null
  const isAppsumo = userSubscription?.isSubscribed && userSubscription.source === 'appsumo'

  const hasActiveSub = subscription?.isSubscribed === true ? subscription : undefined
  const isCanceled = hasActiveSub?.isCanceled
  const currentPeriodEnd = hasActiveSub?.endDate

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl">Subscriptions</CardTitle>
        <CardDescription>Manage your subscription plan and payment methods</CardDescription>
      </CardHeader>
      {subscription?.isSubscribed ? (
        <CardContent>
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between">
                <p className="text-xl font-bold leading-none tracking-tight sm:text-2xl">
                  Current Plan
                </p>
                <p className="text-xl font-bold leading-none tracking-tight sm:text-2xl">
                  {subscriptionPlan?.name}
                </p>
              </CardTitle>

              <CardDescription>
                <p>You are currently on the {subscriptionPlan?.name} plan</p>
                {isCanceled && currentPeriodEnd && (
                  <p className="text-muted-foreground">
                    Subscription ends at {formatDate(currentPeriodEnd)}
                  </p>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {subscriptionPlan?.features.map((f) => (
                  <li key={f.name} className="flex items-center">
                    <Check className="mr-2 h-4 w-4 text-green-500" />
                    {f.name}
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter className="flex items-center justify-start space-x-4">
              <Button onClick={() => navigate('/pricing')}>Update Plan</Button>
              {subscription?.isSubscribed && (
                <Link to={env.VITE_STRIPE_PORTAL_URL} target="_blank">
                  <Button variant="outline">Cancel Subscription</Button>
                </Link>
              )}
            </CardFooter>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl">
                Payment Methods & Billing Information
              </CardTitle>
              <CardDescription>Manage your payment methods and billing information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <Link to={env.VITE_STRIPE_PORTAL_URL} target="_blank">
                  <Button>Go to the Dashboard</Button>
                </Link>
                <p className="text-muted-foreground text-sm">
                  You can manage your payment methods and billing information here.
                </p>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      ) : (
        <CardContent>
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between">
                <p className="text-xl font-bold leading-none tracking-tight sm:text-2xl">
                  Current Plan
                </p>
                <p className="text-xl font-bold leading-none tracking-tight sm:text-2xl">
                  {subscriptionPlan?.name}
                </p>
              </CardTitle>

              <CardDescription>
                You are currently on the {subscriptionPlan?.name} plan
                {isTrial && trialExpiresAt && (
                  <span className="text-muted-foreground ml-1">
                    (expires {formatDate(trialExpiresAt)})
                  </span>
                )}
                {isAppsumo && (
                  <span className="text-muted-foreground ml-1">(lifetime deal via AppSumo)</span>
                )}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <ul className="space-y-2 text-sm">
                {subscriptionPlan?.features.map((f) => (
                  <li key={f.name} className="flex items-center">
                    <Check className="mr-2 h-4 w-4 text-green-500" />
                    {f.name}
                  </li>
                ))}
              </ul>
            </CardContent>
            {(!userSubscription?.isSubscribed || isTrial) && (
              <CardFooter>
                <Button onClick={() => navigate('/pricing')}>
                  {isTrial ? 'Choose your plan' : 'Update Plan'}
                </Button>
              </CardFooter>
            )}
            {isAppsumo && (
              <CardFooter className="flex flex-col items-start gap-2">
                <Link to="https://appsumo.com/account/products/" target="_blank">
                  <Button variant="outline">Manage on AppSumo</Button>
                </Link>
                <p className="text-muted-foreground text-sm">
                  Tier upgrades and refunds are handled through your AppSumo account.
                </p>
              </CardFooter>
            )}
          </Card>
        </CardContent>
      )}
    </Card>
  )
}
